import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  StackDivider,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import CDS from "design-system";
import { TaskTemplate } from "features/Resources/TaskTemplate/models";
import { useForm, useFieldArray, useController, FieldErrors } from "react-hook-form";
import { useNavigate } from "react-router";
import { useCreateTaskTemplate, useUpdateTaskTemplate } from "features/Resources/TaskTemplate/hooks";
import { PrimaryButton } from "components/Button";
import { dirtyValues } from "helpers/form";
import { toTaskTemplateFormData } from "features/Resources/TaskTemplate/helpers";
import ActionRichTextAreaField from "components/FormElements/Fields/TemplateRichTextAreaField";
import ComposeBox, { useComposeBoxColors, BORDERLESS_TRIX_SX } from "features/Resources/Shared/Components/ComposeBox";
import { Path } from "features/Path/models";
import TemplateResourceOptionMenu from "features/Resources/SharedTemplate/Components/TemplateResourceOptionMenu";
import { v2pathPathById } from "helpers/url/pages/v2/pathPath";
import { useCallback, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import NameField from "features/Resources/Shared/Components/Fields/NameField";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import TagByResourceType from "components/Tag/TagByResourceType";
import ArchivedTag from "components/Tag/ArchivedTag";
import BasePathTag from "components/Tag/BasePathTag";
import SelectPathsMenuButton from "features/Path/Components/Button/SelectPathsMenuButton";
import AttributeColumnLabel from "features/Resources/Shared/Components/AttributeColumnLabel";
import { PlusCirlceButtonWithRef } from "components/Button/PlusCircleButton";
import RecordTargetTag from "features/RecordTarget/RecordTargetTag";
import RecordTargetForm from "features/Resources/Shared/Components/Form/RecordTargetForm";
import { RecordTarget } from "features/RecordTarget/models";
import { Schema } from "features/Resources/Shared/Components/Form/RecordTargetForm/schema";
import SchedulingTriggerModalButton from "features/Resources/Shared/Components/Button/SchedulingTriggerModalButton";
import DefaultErrorText from "components/Form/DefaultErrorText";
import TemplateReminderTag from "components/Tag/TemplateReminderTag";
import { usePathsFieldArray } from "features/Resources/Shared/hooks/usePathsFieldArray";
import { RenderTemplateTaskAction, getCombinedMessageErrors } from "../Fields/TemplateTaskActionField";
import { taskTemplateSchema, TaskTemplateSchema } from "../schema";
import AddTemplateReminderButton from "../Components/AddTemplateReminderButton";

// RHF nests each invalid field's error under reminders[i].<field>; count the leaves.
// A FieldError always carries `type`; `message` can be absent (e.g. non-yup resolvers).
function countFieldErrors(node: unknown): number {
  if (!node || typeof node !== "object") return 0;
  const maybeError = node as { type?: unknown };
  if (maybeError.type !== undefined) return 1;
  return Object.values(node).reduce((sum: number, child) => sum + countFieldErrors(child), 0);
}

function summarizeReminderErrors(errors: FieldErrors) {
  const reminders = errors?.reminders;
  if (!Array.isArray(reminders)) return "";

  const parts = reminders.flatMap((reminderErrors, index) => {
    const count = countFieldErrors(reminderErrors);
    if (count === 0) return [];
    return [count > 1 ? `Reminder ${index + 1} has multiple errors` : `Reminder ${index + 1} has an error`];
  });
  return parts.length ? `${parts.join(". ")}.` : "";
}

export default function TaskTemplateForm({ id, data }: { id: string; data: TaskTemplate }) {
  const toast = useToast();
  const navigate = useNavigate();

  const form = useForm<TaskTemplateSchema>({
    mode: "onChange",
    resolver: yupResolver(taskTemplateSchema),
    values: toTaskTemplateFormData(data),
    defaultValues: { paths: [] },
  });

  const { mutate: onUpdate } = useUpdateTaskTemplate(data?.id);
  const { mutate: onCreate } = useCreateTaskTemplate();
  const {
    handleSubmit,
    control,
    formState: { dirtyFields, errors },
  } = form;

  // Paths field logic - using shared hook
  const { pathFields, removePath, addPathId, selectedPathIds, getPathData, returnPathId } = usePathsFieldArray(form);

  // Paths field: Selected paths components
  const selectedPathsComponents = useMemo(
    () =>
      (pathFields || []).map((field: any, index: number) => {
        const path = getPathData(field.id);
        return (
          <BasePathTag
            key={field.fieldId}
            pathId={path?.id}
            pathName={path?.name}
            icon={path?.icon}
            iconColor={path?.iconColor}
            removeable
            onRemove={() => removePath(index)}
          />
        );
      }),
    [pathFields, getPathData, removePath],
  );

  // RecordTarget (Assignee) field logic
  const { field: assigneeField } = useController({ control, name: "assignee" });
  const {
    isOpen: isAssigneePopoverOpen,
    onOpen: onAssigneePopoverOpen,
    onClose: onAssigneePopoverClose,
    getDisclosureProps: getAssigneeDisclosureProps,
  } = useDisclosure();
  const assigneeDisclosureProps = getAssigneeDisclosureProps();

  // Reminders field logic
  const {
    fields: reminderFields = [],
    append: appendReminder,
    update: updateReminder,
    remove: removeReminder,
  } = useFieldArray<TaskTemplateSchema, "reminders", "fieldId">({
    control,
    name: "reminders",
    keyName: "fieldId",
  });

  // RecordTarget (Assignee) field: Submit callback
  const onAssigneeSubmit = useCallback(
    (assigneeData: Schema) => {
      form.setValue("assignee", assigneeData, { shouldValidate: true, shouldDirty: true });
      onAssigneePopoverClose();
    },
    [form, onAssigneePopoverClose],
  );

  // Task Action field: Combined error
  const taskActionError = getCombinedMessageErrors(errors);

  // Reminders field: Summarized error
  const remindersError = summarizeReminderErrors(errors);

  // Compose box colors for the description body bg + hover.
  const { bodyBg: composeBodyBg, bodyHoverBg: composeBodyHoverBg } = useComposeBoxColors();

  // Title row mirrors the NameField above — kept read-only so users have one
  // place to edit it. Clicking shows a toast pointing them at the name field.
  const titleValue = form.watch("name");

  const onSubmit = (formData: Partial<TaskTemplateSchema>) => {
    // For new templates, use all form data. For updates, use only dirty fields.
    // Reminders are always included for updates to allow deletion via reminders: []
    const submitData = id === "new" ? formData : dirtyValues(formData, dirtyFields, ["reminders"]);

    // Early return if updating with no changes
    if (id !== "new" && (!submitData || Object.keys(submitData).length === 0)) {
      return;
    }

    // Convert paths to pathIds if present
    if (submitData.paths) {
      submitData.pathIds = ((submitData?.paths || []) as Path[]).map((path) => path.id);
    }

    if (id === "new") {
      onCreate(submitData, {
        onSuccess: (res: any) => {
          toast({ title: "Task template created.", status: "success" });
          if (returnPathId) {
            navigate(v2pathPathById(returnPathId));
          } else if (formData?.pathIds?.length) {
            navigate(v2pathPathById(formData.pathIds[0]));
          } else {
            navigate(PEOPLE_TEAM_ROUTES.taskTemplates.index);
          }
        },
      });
    } else {
      onUpdate(submitData, { onSuccess: () => toast({ title: "Task template updated.", status: "success" }) });
    }
  };

  return (
    <VStack spacing={4} align="stretch" width="100%">
      {/* Top row: full width */}
      <Flex w="100%" justify="space-between" alignItems="center">
        <TagByResourceType type="task_template" />
        <HStack>
          {data?.archivedAt && <ArchivedTag />}
          <PrimaryButton
            onClick={handleSubmit(onSubmit, (validationErrors) => {
              toast({ title: "Please fix the errors before continuing.", status: "error", duration: 5000 });
              console.error("Form validation errors:", validationErrors);
            })}
          >
            {id === "new" ? "Add" : "Save"}
          </PrimaryButton>
          {data && <TemplateResourceOptionMenu template={data} />}
        </HStack>
      </Flex>

      {/* Name above the attributes row. */}
      <NameField form={form} />

      {/* Template attributes — Starts | Due | Reminders | Path, framed top/bottom,
          vertical dividers between, side-by-side at the left. */}
      <HStack
        divider={<StackDivider borderColor="gray.200" />}
        spacing={0}
        py={4}
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="gray.200"
        align="stretch"
        justify="flex-start"
      >
        <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="200px">
          <AttributeColumnLabel>Starts</AttributeColumnLabel>
          <FormControl isInvalid={!!errors.startAtTrigger}>
            <SchedulingTriggerModalButton
              form={form}
              label="Starts"
              nullLabel="No Start Timing"
              fieldName="startAtTrigger"
              timingOnly={id === "new"}
              excludeTimeOfDay
              allowWeekendsFieldName="allowWeekends"
            />
            <FormErrorMessage mt={0} fontSize="xs">
              {(errors.startAtTrigger as any)?.message}
            </FormErrorMessage>
          </FormControl>
        </VStack>
        <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="200px">
          <AttributeColumnLabel>Due</AttributeColumnLabel>
          <FormControl isInvalid={!!errors.dueAtTrigger}>
            <SchedulingTriggerModalButton
              form={form}
              label="Due"
              fieldName="dueAtTrigger"
              nullLabel="No Due Timing"
              timingOnly
              excludeTimeOfDay
              allowWeekendsFieldName="allowWeekends"
              allowWeekendsReadOnly
            />
            <FormErrorMessage mt={0} fontSize="xs">
              {(errors.dueAtTrigger as any)?.message}
            </FormErrorMessage>
          </FormControl>
        </VStack>
        <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
          <AttributeColumnLabel>Path</AttributeColumnLabel>
          <Flex gap="1" align="center" wrap="wrap">
            {selectedPathsComponents}
            <SelectPathsMenuButton selectedPathIds={selectedPathIds} addPathId={addPathId} />
          </Flex>
        </VStack>
      </HStack>

      {/* Task body — ComposeBox with Assignee + Task Action header rows over a
          rich-text description body. Mirrors the email/message template shape. */}
      <ComposeBox>
        <ComposeBox.HeaderRow label="Title">
          <CDS.Text12
            color={titleValue ? "content.primary" : "fg.subtle"}
            cursor="default"
            onClick={() => toast({
              title: "To change the title, update the name above.",
              status: "info",
              duration: 3000,
            })}
            noOfLines={1}
          >
            {titleValue || "Untitled task"}
          </CDS.Text12>
        </ComposeBox.HeaderRow>
        <ComposeBox.HeaderRow label="Assignee">
          <Popover
            matchWidth
            closeOnBlur={false}
            {...assigneeDisclosureProps}
            onOpen={onAssigneePopoverOpen}
            onClose={onAssigneePopoverClose}
          >
            <FormControl isInvalid={!!errors.assignee}>
              <Flex gap="1" align="center" wrap="wrap">
                {assigneeField.value ? (
                  <RecordTargetTag
                    key={assigneeField.value.id}
                    recordTarget={assigneeField.value as unknown as RecordTarget}
                    remove={() => form.setValue("assignee", null, { shouldDirty: true })}
                  />
                ) : (
                  <PopoverTrigger>
                    <PlusCirlceButtonWithRef label="Add Assignee" />
                  </PopoverTrigger>
                )}
              </Flex>
              <FormErrorMessage mt={0} fontSize="xs">{(errors.assignee as any)?.message}</FormErrorMessage>
            </FormControl>
            <PopoverContent
              pt="3"
              bg="var(--chakra-colors-chakra-body-bg)"
              minW={{ base: "100%", lg: "max-content" }}
              width="100%"
              maxWidth="350px"
              display={isAssigneePopoverOpen ? "block" : "none"}
            >
              <PopoverCloseButton size="xs" sx={{ _hover: { bg: "transparent", color: "fg.emphasized" } }} />
              <PopoverBody>
                <RecordTargetForm
                  label="Assignee"
                  onSubmit={onAssigneeSubmit}
                  recordTargetTypes={["User", "OnboardingJourneyNetworkRelation"]}
                />
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </ComposeBox.HeaderRow>
        <ComposeBox.HeaderRow label="Task Action">
          <Flex direction="column" align="stretch" gap={1}>
            <Flex align="center" wrap="wrap">
              <RenderTemplateTaskAction taskTemplate={data} form={form} />
            </Flex>
            {taskActionError && <DefaultErrorText>{taskActionError}</DefaultErrorText>}
          </Flex>
        </ComposeBox.HeaderRow>
        <Box
          px={4}
          pt={3}
          pb={2}
          bg={composeBodyBg}
          transition="background-color 0.15s"
          sx={{
            ...BORDERLESS_TRIX_SX,
            "&:hover:not(:focus-within)": { bg: composeBodyHoverBg },
          }}
        >
          <ActionRichTextAreaField
            name="description"
            label="Description"
            control={control}
            actionType="task"
            alwaysShowToolbar
            transparent
            hideHeading
            placeholder="Write the task description…"
          />
        </Box>
      </ComposeBox>

      {/* Reminders — own section below the compose box (they're per-task,
          not template-wide metadata, so they read better here than in the
          attributes row at the top). */}
      <VStack spacing={2} align="stretch" pt={2}>
        <AttributeColumnLabel>Reminders</AttributeColumnLabel>
        <Flex gap="1" align="center" wrap="wrap">
          {reminderFields.map((field, i) => (
            <AddTemplateReminderButton
              key={field?.fieldId}
              value={field}
              index={i}
              append={appendReminder}
              update={updateReminder}
              eid={`reminder-${field?.fieldId}`}
            >
              <TemplateReminderTag index={i} reminder={field} onRemove={() => removeReminder(i)} removeable />
            </AddTemplateReminderButton>
          ))}
          <AddTemplateReminderButton
            value={{}}
            index={reminderFields.length}
            append={appendReminder}
            update={updateReminder}
            eid={`reminder-new-${reminderFields.length}`}
            isNew
          />
        </Flex>
        {remindersError && <DefaultErrorText>{remindersError}</DefaultErrorText>}
      </VStack>
    </VStack>
  );
}
